import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FeatureTabData } from "./types";
import { getIconByKey } from "./iconMap";
import FeatureScenario from "./FeatureScenario";

interface FeatureContentProps {
    activeTab: string;
    tabs: FeatureTabData[];
}

const FeatureContent: React.FC<FeatureContentProps> = ({ activeTab, tabs }) => {
    const { t } = useTranslation("nonHealth");
    const activeTabData = tabs.find((tab) => tab.id === activeTab) || tabs[0];

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={activeTab + "-details"}
                className="w-full md:w-2/3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
            >
                {/* عنوان بخش */}
                <motion.div className="relative mb-6" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
                    <h4 className="text-lg font-medium text-gray-800 flex items-center">
                        <motion.div
                            className={`relative inline-flex items-center justify-center ml-3 w-9 h-9 mt-2.5 rounded-full bg-gradient-to-r ${activeTabData.color} text-white overflow-hidden group`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <motion.div animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }} transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}>
                                {getIconByKey(activeTabData.iconKey, "w-5 h-5")}
                            </motion.div>
                        </motion.div>
                        <motion.span className="relative" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
                            <span className="relative z-10">{t("helpTitle")}</span>
                            <motion.div
                                className={`absolute -bottom-1 right-0 h-1 bg-gradient-to-r ${activeTabData.color} rounded-full`}
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 0.8, delay: 0.5 }}
                            />
                        </motion.span>
                    </h4>
                </motion.div>

                {/* توضیحات */}
                <motion.div className="relative mb-5 md:mb-8 pr-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                    <motion.p className="text-gray-700 leading-relaxed">{activeTabData.description}</motion.p>
                    <motion.div
                        className={`absolute -right-2 top-0 bottom-0 w-1 rounded-full bg-gradient-to-b ${activeTabData.color} opacity-50`}
                        initial={{ height: 0 }}
                        animate={{ height: "100%" }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    />
                </motion.div>

                {/* سناریوها */}
                <motion.div className="relative" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {activeTabData.scenarios.map((scenario, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                                className={index >= 2 ? "hidden sm:block" : ""}
                            >
                                <FeatureScenario icon={getIconByKey(scenario.iconKey, "w-5 h-5")} title={scenario.title} description={scenario.description} color={activeTabData.color} />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default FeatureContent;
